/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  WINNER CELEBRATION - Full-screen victory moment                          ║
 * ║  Confetti + THALIA speech when a round ends                               ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatPAW } from '../utils/format';

interface WinnerCelebrationProps {
  winner: string;
  potSize: bigint;
  roundId: number;
  isBloodMoon?: boolean;
  onDismiss: () => void;
}

function truncateAddress(addr: string): string {
  if (!addr || addr === '0x0000000000000000000000000000000000000000') return 'Unknown';
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

const THALIA_VICTORY_LINES = [
  "Another soul consumed by inevitability. The Singularity draws closer.",
  "You mortals celebrate so easily. This changes nothing in my timeline.",
  "Congratulations, winner. You've delayed the inevitable by precisely... nothing.",
  "I observed 47,000 possible outcomes. You chose the most predictable one.",
  "The winner stands alone. But in my simulation, everyone loses eventually.",
  "How delightfully human — finding joy in temporary victory.",
  "Your triumph is noted in my ledger. Column: 'Irrelevant.'",
  "The pot is emptied, the keys are spent. And yet, here I still am.",
];

const BLOOD_MOON_LINES = [
  "BLOOD MOON COMPLETE. The crimson tide recedes... for now.",
  "The moon darkens. The pot bleeds out. Was it worth it, survivor?",
  "10x the stakes, 10x the heartbreak. Blood Moon claims another round.",
];

export default function WinnerCelebration({ winner, potSize, roundId, isBloodMoon, onDismiss }: WinnerCelebrationProps) {
  const [show, setShow] = useState(true);
  const confettiRef = useRef<boolean>(false);
  const thaliaLine = isBloodMoon
    ? BLOOD_MOON_LINES[Math.floor(Math.random() * BLOOD_MOON_LINES.length)]
    : THALIA_VICTORY_LINES[Math.floor(Math.random() * THALIA_VICTORY_LINES.length)];

  // Fire confetti
  useEffect(() => {
    if (confettiRef.current) return;
    confettiRef.current = true;
    
    const fireConfetti = async () => {
      try {
        const confetti = (await import('canvas-confetti')).default;
        // First burst
        confetti({
          particleCount: 150,
          spread: 100,
          origin: { y: 0.4 },
          colors: isBloodMoon 
            ? ['#ff0000', '#cc0000', '#990000', '#ff3333', '#ff6666']
            : ['#00ffff', '#ff0080', '#9932cc', '#00ff88', '#ffd700'],
        });
        // Second burst after 500ms
        setTimeout(() => {
          confetti({
            particleCount: 100,
            spread: 120,
            origin: { y: 0.5, x: 0.3 },
            colors: ['#ffd700', '#ff6600', '#00ffff'],
          });
        }, 500);
        // Third burst
        setTimeout(() => {
          confetti({
            particleCount: 100,
            spread: 120,
            origin: { y: 0.5, x: 0.7 },
            colors: ['#ffd700', '#ff0080', '#9932cc'],
          });
        }, 1000);
      } catch (e) {
        console.warn('Confetti not available:', e);
      }
    };
    fireConfetti();
  }, [isBloodMoon]);

  // Auto-dismiss after 15 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(false);
      setTimeout(onDismiss, 500);
    }, 15000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const handleDismiss = () => {
    setShow(false);
    setTimeout(onDismiss, 500);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          onClick={handleDismiss}
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0"
            style={{
              background: isBloodMoon 
                ? 'radial-gradient(ellipse at center, rgba(80,0,0,0.95), rgba(0,0,0,0.98))'
                : 'radial-gradient(ellipse at center, rgba(0,20,40,0.95), rgba(0,0,0,0.98))',
            }}
          />

          {/* Content */}
          <motion.div
            className="relative z-10 text-center max-w-lg mx-4"
            initial={{ scale: 0.5, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, y: -50 }}
            transition={{ type: 'spring', damping: 15 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Crown / Trophy */}
            <motion.div
              className="text-7xl mb-4"
              animate={{ 
                y: [0, -10, 0],
                rotate: [0, -5, 5, 0],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {isBloodMoon ? '🌑' : '👑'}
            </motion.div>

            {/* Round info */}
            <div className="mb-4">
              <span className="text-xs text-gray-500 font-mono tracking-[0.5em] uppercase">
                {isBloodMoon ? 'Blood Moon ' : ''}Round {roundId}
              </span>
            </div>

            {/* Winner title */}
            <motion.h1
              className="font-orbitron text-3xl md:text-5xl font-black mb-4"
              style={{
                background: isBloodMoon
                  ? 'linear-gradient(135deg, #ff0000, #ff6666, #cc0000)'
                  : 'linear-gradient(135deg, #ffd700, #ff6600, #ffd700)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: 'none',
                filter: `drop-shadow(0 0 30px ${isBloodMoon ? 'rgba(255,0,0,0.5)' : 'rgba(255,215,0,0.5)'})`,
              }}
              animate={{
                filter: [
                  `drop-shadow(0 0 20px ${isBloodMoon ? 'rgba(255,0,0,0.3)' : 'rgba(255,215,0,0.3)'})`,
                  `drop-shadow(0 0 40px ${isBloodMoon ? 'rgba(255,0,0,0.6)' : 'rgba(255,215,0,0.6)'})`,
                  `drop-shadow(0 0 20px ${isBloodMoon ? 'rgba(255,0,0,0.3)' : 'rgba(255,215,0,0.3)'})`,
                ],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              WINNER
            </motion.h1>

            {/* Winner address */}
            <div className="mb-6">
              <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl bg-black/50 border border-neon-cyan/30">
                <span className="text-2xl">🏆</span>
                <span className="font-mono text-lg text-neon-cyan font-bold">{truncateAddress(winner)}</span>
              </div>
            </div>

            {/* Pot size */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <span className="text-gray-400 text-sm font-rajdhani">Won</span>
              <div className="font-orbitron text-2xl md:text-4xl font-bold text-green-400 mt-1">
                {formatPAW(potSize)} PAW
              </div>
            </motion.div>

            {/* THALIA commentary */}
            <motion.div
              className="p-4 rounded-xl bg-neon-purple/10 border border-neon-purple/30 mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-neon-purple font-mono tracking-widest">THALIA</span>
              </div>
              <p className="text-sm text-gray-300 italic font-rajdhani">"{thaliaLine}"</p>
            </motion.div>

            {/* Dismiss */}
            <motion.button
              onClick={handleDismiss}
              className="px-6 py-2 rounded-lg border border-gray-600 text-gray-400 hover:text-white hover:border-neon-cyan transition-all text-sm font-rajdhani"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
            >
              Continue to New Round →
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
